import { LitElement, PropertyValues, css, html } from 'lit';
import { customElement, query, queryAll } from 'lit/decorators.js';
import { getGlobalStyleSheets } from '../../global-styles';

import * as TagNames from './progress-stepper.tagnames';
import { AtlProgressStepperItem } from './progress-stepper-item.component';

@customElement(TagNames.ProgressStepper)
export class AtlProgressStepper extends LitElement {
   declare vertical?: boolean;

   private _observer: MutationObserver;

   static properties = {
      vertical: { type: Boolean },
   };

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         atlantis-progress-step {
            flex: 1 1 0%;
            display: flex;
            flex-direction: column;
            align-items: center;
         }

         .vertical atlantis-progress-step {
            flex-direction: row !important;
         }

         atlantis-icon {
            display: flex;
            justify-content: center;
            align-items: center;
         }

         .progress-stepper {
            justify-content: center;
         }
         .progress-stepper.vertical {
            flex-direction: column;
         }

         .progress-stepper.vertical > .progress-step {
            max-width: 100%;
            min-width: 70px;
            align-items: center;
            flex-direction: row;
         }

         .progress-stepper.vertical > .progress-step:not(:first-child) {
            margin-top: 10px;
         }

         .vertical > .step-body {
            margin-left: 20px;
            padding-left: 20px;
            align-items: flex-start;
         }

         .vertical > .step-header {
            width: 24px !important;
            height: 24px !important;
            position: absolute !important;
         }

         .progress-stepper.vertical
            > .progress-step.step-stroke:not(:last-child)::after {
            content: '';
            position: relative;
            height: var(--step-stroke-height, 0px);
            left: calc(clamp(1.7rem, 2.5vw, 1.5rem) / 2);
            width: 2px !important;
            top: calc(clamp(1.7rem, 2.5vw, 1.5rem) / 2 + 15px);
            margin-left: -2px;
         }
                
        .progress-step-header {
            cursor: pointer;
        } 
      `,
   ];

   constructor() {
      super();
      this._observer = new MutationObserver(this._handleMutations.bind(this));
   }

   disconnectedCallback(): void {
      this._observer.disconnect();
      super.disconnectedCallback();
   }

   @query('ol')
   listElement?: HTMLElement;

   @queryAll(TagNames.ProgressStepperItem)
   progressStepperItems?: HTMLElement[];

   firstUpdated() {
      const slot = this.shadowRoot?.querySelector('slot');
      slot?.addEventListener('slotchange', this._handleSlotChange);
      this._handleSlotChange();
   }

   protected override updated(changedProperties: PropertyValues): void {
      if (changedProperties.has('vertical') && this.listElement) {
         this.listElement?.classList?.remove('vertical');
         if (this.progressStepperItems) {
            this.progressStepperItems.forEach((step) => {
               step.classList.remove('vertical');
            });
         }

         if (this.vertical) {
            this.listElement?.classList?.add('vertical');
            if (this.progressStepperItems) {
               this._applyVerticalStyles(
                  this.progressStepperItems,
                  this.listElement
               );
            }
         }
      }
   }

   private _applyVerticalStyles(
        atlantisProgressSteps: HTMLElement[],
        ol: HTMLElement
    ): void {
        atlantisProgressSteps.forEach((step, index) => {
            step.classList.add('vertical');
            requestAnimationFrame(() => {
                this._setStokeHeight(step, index, ol);
            });
        });
    }


    private _setStokeHeight(
        step: HTMLElement,
        index: number,
        ol: HTMLElement
    ): void {
        const strokeHeight = step.getBoundingClientRect().height;
        if (strokeHeight < 1){
            setTimeout(() => {
                return this._setStokeHeight(step, index, ol)
            }, 500)
            return;
        }
        const li = ol.children[index] as HTMLElement;
        if (li?.classList.contains('step-stroke')) {
            li.style.setProperty(
            '--step-stroke-height',
            `${strokeHeight + 10}px`
            );
        }
    }

   private _decorateStepListItem(step: AtlProgressStepperItem) {
      const li = step.parentElement as HTMLElement;
      if (!li) return;

      if (step.showStroke) {
         li.classList.add('step-stroke');
      } else {
         li.classList.remove('step-stroke');
      }

      if (step.active) {
         li.classList.add('active');
      } else {
         li.classList.remove('active');
      }

      if (step.activeStroke) {
         li.classList.add('stroke-active');
      } else {
         li.classList.remove('stroke-active');
      }
   }

   private _handleSlotChange = () => {
      const slot = this.shadowRoot?.querySelector('slot');

      const assignedNodes = slot
         ?.assignedNodes({ flatten: false })
         ?.filter(
            (node) =>
               node.nodeType === Node.ELEMENT_NODE &&
               (node as HTMLElement).tagName ===
                  TagNames.ProgressStepperItem.toUpperCase()
         )
         .map((node) => node as AtlProgressStepperItem);

      const ol = document.createElement('ol');
      ol.classList.add('progress-stepper');

      if (this.vertical) {
         ol.classList.add('vertical');
      }

      assignedNodes?.forEach((node, index) => {
         const li = document.createElement('li');
         li.id = `node-${index}`;
         li.appendChild(node);
         li.classList.add('progress-step');

         this._decorateStepListItem(node);

         ol.appendChild(li);
      });

      slot?.parentNode!.replaceChild(ol, slot);

      if (this.progressStepperItems && this.vertical) {
         this._applyVerticalStyles(this.progressStepperItems!, ol);
      }

      this._observeMutations();
   };

   private _observeMutations() {
      this._observer.disconnect();
      this._observer.observe(this.listElement!, {
         attributes: true,
         childList: true,
         subtree: true,
      });
   }

   private _handleMutations(mutations: MutationRecord[]) {
      mutations.forEach((mutation) => {
         if (mutation.type === 'attributes') {
            this._decorateStepListItem(
               mutation.target as AtlProgressStepperItem
            );
         }
         else if (mutation.type === 'childList'){
            this._decorateStepListItem(
                mutation?.target?.parentElement as AtlProgressStepperItem,
            );
         } 
      });
   }

   render() {
      return html`<slot></slot>`;
   }
}
